import type { Email, ThreadMessage } from '@/types/email';

const FALLBACK_FOLDER_KEY = 'unknown';

export interface MergeUpdateOptions {
  /**
   * When true, allows empty arrays to replace non-empty arrays.
   * Use this for explicit user actions (like removing all labels).
   * Default false preserves existing arrays when incoming is empty.
   */
  allowEmptyArrayReplace?: boolean;
}

/**
 * Detail-level fields that should not be overwritten by null/empty values
 * from partial updates (list-level fetches, realtime events).
 * These fields are only present in detail responses and would be lost
 * if a partial update (which has them as null/empty) overwrites them.
 */
const DETAIL_FIELDS = new Set([
  'bodyHtml',
  'bodyText',
  'body',
  'headers',
  'ocrText',
  'ocrStatus',
  'ocrMetadata',
  'ocrProcessedAt',
]);

/**
 * Merges an incoming email update into an existing email, preserving existing
 * values for properties that are undefined in the incoming email.
 * This prevents partial updates (like realtime events) from overwriting
 * bodyHtml and other fields with undefined.
 *
 * Also preserves existing array values (like autoLabels) when the incoming
 * value is an empty array, since partial updates often don't include full
 * array data. Use allowEmptyArrayReplace: true to override this behavior
 * for explicit user actions.
 *
 * Detail-level fields (bodyHtml, body, headers, etc.) are protected from
 * being overwritten by null/empty values from partial updates.
 */
export function mergeEmailUpdate<T extends Email | ThreadMessage>(
  existing: T,
  incoming: Partial<T>,
  options: MergeUpdateOptions = {}
): T {
  const { allowEmptyArrayReplace = false } = options;

  // First pass: check if any values actually changed
  let hasChanges = false;
  for (const key of Object.keys(incoming)) {
    const value = incoming[key as keyof T];
    if (value === undefined) {
      continue;
    }
    // Check empty array preservation rule
    if (!allowEmptyArrayReplace && Array.isArray(value) && value.length === 0) {
      const existingValue = existing[key as keyof T];
      if (Array.isArray(existingValue) && existingValue.length > 0) {
        continue;
      }
    }
    // Protect detail-level fields from being wiped by partial updates
    if (DETAIL_FIELDS.has(key)) {
      const existingValue = existing[key as keyof T];
      if (existingValue != null && existingValue !== '' && (value == null || value === '')) {
        continue;
      }
    }
    // Compare values - use strict equality for primitives, reference equality for objects
    const existingValue = existing[key as keyof T];
    if (existingValue !== value) {
      hasChanges = true;
      break;
    }
  }

  // If nothing changed, return the original object to preserve reference equality
  if (!hasChanges) {
    return existing;
  }

  // Second pass: create merged object only if there are actual changes
  const merged = { ...existing } as T & Record<string, unknown>;
  for (const key of Object.keys(incoming)) {
    const value = incoming[key as keyof T];
    if (value === undefined) {
      continue;
    }
    // Preserve existing non-empty arrays when incoming is an empty array
    // This prevents partial updates from clearing labels, attachments, etc.
    // Unless allowEmptyArrayReplace is true (for explicit user actions)
    if (!allowEmptyArrayReplace && Array.isArray(value) && value.length === 0) {
      const existingValue = existing[key as keyof T];
      if (Array.isArray(existingValue) && existingValue.length > 0) {
        continue;
      }
    }
    // Protect detail-level fields from being wiped by partial updates
    if (DETAIL_FIELDS.has(key)) {
      const existingValue = existing[key as keyof T];
      if (existingValue != null && existingValue !== '' && (value == null || value === '')) {
        continue;
      }
    }
    merged[key] = value;
  }
  return merged;
}

export interface MergeEmailOptions {
  /**
   * When true, projectEmailsFromIndex returns one row per message (date-sorted)
   * instead of collapsing thread members into a single representative row.
   *
   * The sender digest needs this: it renders an individual card per message and
   * its `totalMessages` count is message-level (backend `count(*)`). Collapsing
   * threads there would (a) hide thread-member messages behind a single card and
   * (b) make the loaded row count permanently less than `totalMessages`, which
   * drives an infinite "load more" loop (the load-more sentinel never retires).
   */
  disableThreading?: boolean;
  /**
   * Read-time visibility filter applied BEFORE thread collapsing. Emails failing
   * the filter stay in the index (so "restore" is just re-projecting without the
   * filter hit) but never reach the rendered list. The email store passes the
   * suppression filter (recently-removed grace + sender-being-archived) here so
   * NO write path — realtime hydration, digest close, direct merges — can
   * resurrect an optimistically removed email.
   */
  visibilityFilter?: (email: Email) => boolean;
}

export type EmailIndex = Record<string, Email>;

/**
 * Reverse lookup: email.id → identity key in the EmailIndex.
 * Eliminates O(n) scans when finding an email by its ID.
 */
export type EmailReverseIndex = Map<string, string>;

export function buildReverseIndex(index: EmailIndex): EmailReverseIndex {
  const reverse = new Map<string, string>();
  for (const [identity, email] of Object.entries(index)) {
    if (email?.id) {
      reverse.set(email.id, identity);
    }
  }
  return reverse;
}

export function findIdentityById(reverseIndex: EmailReverseIndex, emailId: string): string | null {
  return reverseIndex.get(emailId) ?? null;
}

export function getEmailIdentity(email: Email): string {
  const { accountId, id, messageId, uid } = email;
  const folderKey = email.folderId ?? FALLBACK_FOLDER_KEY;
  if (id) {
    return `${accountId}:${id}`;
  }
  if (messageId) {
    return `${accountId}:${folderKey}:${messageId}`;
  }
  if (uid) {
    return `${accountId}:${folderKey}:${uid}`;
  }
  const timestamp = getEmailTimestamp(email);
  return `${accountId}:${folderKey}:${timestamp}:${email.subject}`;
}

export function getEmailTimestamp(email: Email): number {
  if (email.date instanceof Date) {
    return email.date.getTime();
  }
  if (typeof email.date === 'string' || typeof email.date === 'number') {
    const parsed = new Date(email.date);
    const time = parsed.getTime();
    if (Number.isFinite(time)) {
      return time;
    }
  }
  if (
    email.date &&
    typeof (email.date as unknown as { getTime?: () => number }).getTime === 'function'
  ) {
    const time = (email.date as unknown as Date).getTime();
    if (Number.isFinite(time)) {
      return time;
    }
  }
  return 0;
}

/**
 * Compare emails by date (descending), with stable secondary sort by ID.
 * Using ID as tie-breaker ensures consistent ordering for emails with identical timestamps.
 */
const compareByDateDesc = (a: Email, b: Email): number => {
  const timeDiff = getEmailTimestamp(b) - getEmailTimestamp(a);
  if (timeDiff !== 0) {
    return timeDiff;
  }
  // Stable secondary sort by ID to prevent random reordering of same-timestamp emails
  const aId = a.id ?? '';
  const bId = b.id ?? '';
  if (aId < bId) return -1;
  if (aId > bId) return 1;
  return 0;
};

function mergeThreadEntries(existing: Email, incoming: Email): Email {
  const existingTime = getEmailTimestamp(existing);
  const incomingTime = getEmailTimestamp(incoming);
  const prefersIncoming = Number.isFinite(incomingTime) && incomingTime >= existingTime;
  const preferred = prefersIncoming ? incoming : existing;
  const secondary = prefersIncoming ? existing : incoming;

  // read = AND is INTENTIONAL: a collapsed thread row shows unread if ANY member
  // is unread (Gmail-like). Do NOT change this to OR — that would mark a thread
  // with unread replies as read. To turn a thread row read, mark-read must flip
  // EVERY member (see getThreadMemberIds + the bulk markRead expansion), after
  // which this AND naturally becomes true.
  const aggregatedRead = existing.read && incoming.read;
  const aggregatedStarred = existing.starred || incoming.starred;

  const messageCount = Math.max(
    preferred.messageCount ?? 1,
    secondary.messageCount ?? 1
  );

  const senderMailboxCount = Math.max(
    preferred.senderMailboxCount ?? 0,
    secondary.senderMailboxCount ?? 0
  );

  return {
    ...preferred,
    read: aggregatedRead,
    starred: aggregatedStarred,
    messageCount:
      messageCount > 1 ? messageCount : preferred.messageCount ?? secondary.messageCount,
    senderMailboxCount: senderMailboxCount || preferred.senderMailboxCount || secondary.senderMailboxCount,
  };
}

export function createEmailIndex(source: Iterable<Email> = []): EmailIndex {
  const index: EmailIndex = {};
  for (const email of source) {
    const identity = getEmailIdentity(email);
    index[identity] = email;
  }
  return index;
}

export function upsertEmailIndex(
  index: EmailIndex,
  emails: Iterable<Email>,
  options: MergeUpdateOptions = {},
): EmailIndex {
  let next: EmailIndex | null = null;
  for (const email of emails) {
    if (!email) {
      continue;
    }
    const identity = getEmailIdentity(email);
    const current = (next ?? index)[identity];
    if (current === email) {
      continue;
    }
    if (!next) {
      next = { ...index };
    }
    // Merge with existing email to preserve autoLabels and other array properties
    // that may not be included in partial updates. Callers merging an
    // AUTHORITATIVE full payload (e.g. loadEmails) pass allowEmptyArrayReplace so
    // a server-cleared array (labels removed) is honored instead of preserved.
    next[identity] = current ? mergeEmailUpdate(current, email, options) : email;
  }
  return next ?? index;
}

export function pruneEmailIndexByIds(index: EmailIndex, ids: Set<string>): EmailIndex {
  if (!ids.size) {
    return index;
  }
  let next: EmailIndex | null = null;
  Object.entries(index).forEach(([identity, email]) => {
    if (!email || !ids.has(email.id)) {
      if (next) {
        next[identity] = email;
      }
      return;
    }
    if (!next) {
      next = { ...index };
    }
    delete next[identity];
  });
  return next ?? index;
}

/**
 * Projects an EmailIndex into a sorted array of emails, merging threads.
 * When `previous` is provided, returns the same array reference if the
 * projected result is identical (same emails in same order by reference),
 * preventing unnecessary React re-renders downstream.
 */
export function projectEmailsFromIndex(
  index: EmailIndex,
  options: MergeEmailOptions = {},
  previous?: Email[],
): Email[] {
  let emails = Object.values(index);
  if (options.visibilityFilter) {
    emails = emails.filter(options.visibilityFilter);
  }
  if (!emails.length) {
    return previous?.length === 0 ? previous : [];
  }

  let result: Email[];
  if (options.disableThreading) {
    // One row per message (still deduped by identity in the index). Used by the
    // sender digest — see MergeEmailOptions.disableThreading.
    result = [...emails].sort(compareByDateDesc);
  } else {
    // Threading enabled - group emails by threadId into a single representative row
    const threadMap = new Map<string, Email>();
    const singles: Email[] = [];

    emails.forEach((email) => {
      if (!email.threadId) {
        singles.push(email);
        return;
      }
      const folderKey = email.folderId ?? FALLBACK_FOLDER_KEY;
      const key = `${email.accountId}:${folderKey}:${email.threadId}`;
      const existing = threadMap.get(key);
      if (!existing) {
        threadMap.set(key, email);
        return;
      }
      threadMap.set(key, mergeThreadEntries(existing, email));
    });

    result = [...threadMap.values(), ...singles].sort(compareByDateDesc);
  }

  // Stabilize reference: if identical to previous, return previous to avoid re-renders
  if (previous && previous.length === result.length) {
    let same = true;
    for (let i = 0; i < result.length; i++) {
      if (result[i] !== previous[i]) {
        same = false;
        break;
      }
    }
    if (same) {
      return previous;
    }
  }

  return result;
}

/**
 * Returns the ids of every loaded message that collapses into the same thread row
 * as `repEmail` (including the rep itself). The list shows one representative per
 * thread; a mark-read on that row must flip ALL members so the read=AND
 * aggregation in mergeThreadEntries turns the row read. Uses the exact same
 * thread key as projectEmailsFromIndex so it matches the rendered collapse.
 * Returns [repEmail.id] when the email has no thread.
 */
export function getThreadMemberIds(
  index: EmailIndex,
  repEmail: Pick<Email, 'id' | 'accountId' | 'folderId' | 'threadId'>
): string[] {
  if (!repEmail?.threadId) {
    return repEmail?.id ? [repEmail.id] : [];
  }
  const folderKey = repEmail.folderId ?? FALLBACK_FOLDER_KEY;
  const targetKey = `${repEmail.accountId}:${folderKey}:${repEmail.threadId}`;
  const ids: string[] = [];
  for (const key in index) {
    const email = index[key];
    if (!email || !email.threadId || !email.id) {
      continue;
    }
    const emailKey = `${email.accountId}:${email.folderId ?? FALLBACK_FOLDER_KEY}:${email.threadId}`;
    if (emailKey === targetKey) {
      ids.push(email.id);
    }
  }
  if (!ids.length && repEmail.id) {
    ids.push(repEmail.id);
  }
  return ids;
}

/**
 * Legacy helper that merges two arrays of emails using identity deduplication and optional threading.
 * Prefer using indexes (createEmailIndex/upsertEmailIndex/projectEmailsFromIndex) for new code.
 */
export function mergeEmailCollections(
  existing: Email[],
  incoming: Email[],
  options: MergeEmailOptions = {}
): Email[] {
  if (!existing.length && !incoming.length) {
    return [];
  }
  const baseIndex = createEmailIndex(existing);
  const mergedIndex = upsertEmailIndex(baseIndex, incoming);
  return projectEmailsFromIndex(mergedIndex, options);
}
